import React from "react";
import MessageCard from "./Message";
import MessageSkeleton from "./MessageSkeleton";
import "./messages.css";

// Breakpoints must stay in sync with the layout widths in messages.css
const getColumnCount = () =>
  window.matchMedia("(min-width: 1100px)").matches
    ? 3
    : window.matchMedia("(min-width: 700px)").matches
    ? 2
    : 1;

function useColumnCount() {
  const [count, setCount] = React.useState(getColumnCount);

  React.useEffect(() => {
    const queries = ["(min-width: 1100px)", "(min-width: 700px)"].map((q) =>
      window.matchMedia(q)
    );
    const onChange = () => setCount(getColumnCount());
    queries.forEach((mq) => mq.addEventListener("change", onChange));
    return () =>
      queries.forEach((mq) => mq.removeEventListener("change", onChange));
  }, []);

  return count;
}

// Row-wise round-robin: item i goes to column i % N, so reading across the
// top row is always the most recent messages, in order.
function splitIntoColumns(items, columnCount) {
  const columns = Array.from({ length: columnCount }, () => []);
  items.forEach((item, i) => {
    columns[i % columnCount].push({ item, index: i });
  });
  return columns;
}

const MessageList = ({
  messages,
  loading = false,
  hasMore = false,
  loadMore,
  onMessageClick,
  labels,
}) => {
  const sentinelRef = React.useRef(null);
  const columnCount = useColumnCount();

  React.useEffect(() => {
    if (!hasMore || !loadMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "400px" }
    );

    const node = sentinelRef.current;
    if (node) observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  if (loading) {
    return (
      <div className="message-grid-container">
        <div className="message-grid">
          {splitIntoColumns([...Array(6)], columnCount).map((column, c) => (
            <div key={c} className="message-column">
              {column.map(({ index }) => (
                <div
                  key={index}
                  className="message-card-wrapper"
                  style={{ "--message-index": index }}
                >
                  <MessageSkeleton />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="message-grid-container">
        <div className="wall-state">
          <div className="wall-state-icon">💭</div>
          <h3 className="wall-state-title">No messages yet!</h3>
          <p className="wall-state-text">
            Be the first to share your thoughts and sign our guest book.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="message-grid-container">
      <div className="message-grid">
        {splitIntoColumns(messages, columnCount).map((column, c) => (
          <div key={c} className="message-column">
            {column.map(({ item: msg, index }) => (
              <div
                key={msg.id || index}
                className="message-card-wrapper"
                style={{ "--message-index": index }}
              >
                <MessageCard
                  name={msg.name}
                  country={msg.country}
                  city={msg.city}
                  message={msg.message}
                  date={msg.timestamp}
                  labels={labels}
                  onClick={
                    onMessageClick ? () => onMessageClick(msg) : undefined
                  }
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      {hasMore && (
        <div className="load-more-row" ref={sentinelRef}>
          <button type="button" className="load-more-button" onClick={loadMore}>
            {labels.loadMore}
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageList;
